import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DocumentMode } from '../shared/enums/document-mode.enum';
import { FileExtensionGroups } from '../shared/enums/app-constants';
import { SocketService } from './socket.service';
import { BizEntityTypeCategory } from '@optima-stride-ai/core';
import { WopiServerStateStoreService } from './state-store/wopi-server-state-store/wopi-server-state-store.service';

/**
 * Interface for Collabora iframe URL response
 */
export interface CollaboraIframeUrlResponse {
  url: string;
  wopiSrc: string;
  accessToken: string;
  accessTokenTtl: number;
}

/**
 * Interface for file upload response
 */
export interface CollaboraUploadResponse {
  success: boolean;
  path: string;
  filename: string;
}

/**
 * CollaboraService
 * 
 * Handles integration with Collabora Online for document editing
 * - Generate iframe URLs for opening documents in Collabora
 * - Upload files to Collabora server
 * - Check if file types are supported by Collabora
 */
@Injectable({
  providedIn: 'root'
})
export class CollaboraService {
  private socketService = inject(SocketService);
  private wopiServerStateStoreService = inject(WopiServerStateStoreService);

  private socketInitialized = false;

  /**
   * Build HTTP headers dynamically from environment
   */
  private getHeaders(): { 'authorization': string; 'appuuid': string; 'tenantuuid': string } {
    return {
      'authorization': environment.hayagriva.headers.userToken || '',
      'appuuid': environment.hayagriva.headers.appid || '',
      'tenantuuid': environment.hayagriva.headers.tenantuuid || ''
    };
  }

  /**
   * Supported file extensions for Collabora Online
   */
  private readonly supportedExtensions = FileExtensionGroups.COLLABORA_SUPPORTED;

  /**
   * Initialize socket connection for WOPI server communication
   * This is called automatically when Collabora features are used
   */
  private initializeSocketConnection(): void {
    if (this.socketInitialized) {
      return;
    }

    // Connect to WOPI server
    this.socketService.connect();

    // REMOVED: Global initialization - now done per-file in CollaboraViewerComponent
    // Each file viewer calls wopiApiProxyService.initializeForFile(fileId) to prevent
    // cross-file event pollution
    // this.wopiApiProxyService.initialize(); // DEPRECATED

    this.socketInitialized = true;
  }

  /**
   * Check if a file is supported by Collabora Online
   * 
   * @param filename - Name of the file to check
   * @returns True if the file can be opened in Collabora
   */
  // isFileSupported(filename: string): boolean {
  //   const lowerName = filename.toLowerCase();
  //   return this.supportedExtensions.some(ext => lowerName.endsWith(ext));
  // }
  isFileSupported(category: BizEntityTypeCategory): boolean {
    return this.supportedExtensions.some(ext => category === ext);
  }


  /**
   * Get the Collabora iframe URL for a document
   * 
   * @param filename - Name of the file to open
   * @param mode - 'edit' or 'view' mode
   * @param userId - User ID (optional)
   * @param userName - User display name (optional)
   * @param tenantId - Tenant ID (optional)
   * @returns Observable with iframe URL and WOPI details
   */
  getIframeUrl(
    filename: string,
    mode: DocumentMode = DocumentMode.Edit,
    userId?: string,
    userName?: string,
    tenantId?: string
  ): Observable<CollaboraIframeUrlResponse> {
    // Initialize socket connection for WOPI communication
    this.initializeSocketConnection();

    // Convert Promise to Observable and transform the response
    return from(
      this.wopiServerStateStoreService.getIframeUrl(filename, mode, userId, userName, tenantId)
    ).pipe(
      map((data: any) => ({
        url: data.url || '',
        wopiSrc: data.wopiSrc || '',
        accessToken: data.accessToken || '',
        accessTokenTtl: data.accessTokenTtl || 0
      } as CollaboraIframeUrlResponse)),
      catchError((error) => {
        console.error('CollaboraService.getIframeUrl - WOPI Server State Store Error:', error);
        return from([{
          url: '',
          wopiSrc: '',
          accessToken: '',
          accessTokenTtl: 0
        } as CollaboraIframeUrlResponse]);
      })
    );
  }

  /**
   * Generate thumbnail for a Collabora file using the Collabora thumbnail API
   * This method sends the file to the server which then uses Collabora's thumbnail generation
   * 
   * @param file - File to generate thumbnail for
   * @returns Promise with base64 thumbnail data or null
   */
  async generateThumbnailForCollaboraFile(file: File): Promise<string | null> {
    try {
      // Create FormData and upload file
      const formData = new FormData();
      formData.append('data', file);

      // Call the WOPI server state store service to generate thumbnail
      const response: any = await this.wopiServerStateStoreService.getCollaboraThumbnail(formData);
      if (!response) {
        return null;
      }

      // Convert ArrayBuffer to base64
      const base64 = this.arrayBufferToBase64(response);
      return `data:image/png;base64,${base64}`;
    } catch (error) {
      console.error('CollaboraService.generateThumbnailForCollaboraFile - Error:', error);
      return null;
    }
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
